import React, { useEffect, useState, memo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { creditorGroup } from "us.collection.admin/actions";
import {
  SaveCreditorGroup,
  UpdateCreditorGroup,
} from "us.collection.admin/repository";
import { $Select } from "us.common/components";
import { RootState } from "us.helper/types";
import { INewCreditorGroup } from "../../Types";
import { CreditorGroupSchema } from "../../Validations";

const { $Input, $Button, $Form, $Popconfirm, $TextArea } = Common.Components;

const NewCreditorGroup: React.FC<INewCreditorGroup & PropsFromRedux> = memo(
  (props) => {
    const { t } = useTranslation();
    const {
      creditorGroupDetails,
      saveCreditorGroup,
      updateCreditorGroup,
      drawer,
      onClose,
      creditorGroups,
    } = props;

    const { data } = creditorGroups ?? {};
    const { data: creditorGroupData, isFetching } = creditorGroupDetails ?? {};

    const handleSubmit = (values: any, actions: any) => {
      actions.setSubmitting(true);
      if (actions.isValidating) return;
      if (drawer?.isNewCreditorGroup) {
        const creditorGroupReq = SaveCreditorGroup.call(values);
        saveCreditorGroup && saveCreditorGroup(creditorGroupReq);
      } else {
        const creditorGroupReq = UpdateCreditorGroup.call(values);
        updateCreditorGroup && updateCreditorGroup(creditorGroupReq);
      }
    };

    return (
      <Formik
        initialValues={{ ...creditorGroupData }}
        validationSchema={CreditorGroupSchema}
        validateOnChange
        validateOnBlur
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValidating,
          resetForm,
          ...rest
        }: any) => (
          <$Form>
            <div>
              <div className="mb-3">
                <$Input
                  label={t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NAME")}
                  name="groupName"
                  required
                />
              </div>
              <div>
                <$TextArea
                  label={t(
                    "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.DESCRIPTION"
                  )}
                  name="description"
                  autoSize={{ minRows: 8, maxRows: 8 }}
                  required
                  showCount
                  maxLength={100}
                />
              </div>
              {drawer?.isNewCreditorGroup && (
                <div className="mt-3">
                  <span>
                    {t(
                      "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.SELECT_EXISTING_GROUP_AS_A_TEMPLATE"
                    )}
                  </span>
                  <$Select
                    className="w-100"
                    showSearch={false}
                    options={data}
                    allOption={false}
                    optionValue="id"
                    optionText="groupName"
                    formitem={{}}
                    name="templateGroupId"
                    dropdownMatchSelectWidth={false}
                  />
                </div>
              )}
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  className="mr-2"
                  disabled={
                    !rest.isValid || isSubmitting || !rest.dirty || isFetching
                  }
                  loading={isFetching}
                  onClick={(e) => {
                    handleSubmit(e);
                  }}
                  type="primary"
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
                {/* {setIsEdited(rest.dirty)} */}
                {rest.dirty && (
                  <$Popconfirm
                    title={t(
                      "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.ADDNEWCANCELMSG"
                    )}
                    placement="topLeft"
                    onConfirm={() => onClose()}
                    okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                    cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                  >
                    <$Button>{t("US.COLLECTION.COMMON:COMMON.CANCEL")}</$Button>
                  </$Popconfirm>
                )}
                {!rest.dirty && (
                  <$Button onClick={() => onClose()}>
                    {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </$Form>
        )}
      </Formik>
    );
  }
);

const mapStateToProps = (state: RootState) => {
  const { creditorGroup } = state;
  const { creditorGroupDetails, creditorGroups, drawer } = creditorGroup;

  return { creditorGroupDetails, drawer, creditorGroups };
};

const mapDispatchToProps = {
  saveCreditorGroup: creditorGroup.creditorGroup.save,
  updateCreditorGroup: creditorGroup.creditorGroup.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(NewCreditorGroup);
